import moment from 'moment';
import {ouTraco} from '../inscricao/documentoInscricao.js';
import {FORMATO_DATA_WIRE, formatarCpf, formatarData} from '../processo/processoSelecao.js';

/*
 * Regras puras da Carga SAF do administrativo (Pbest e outros, Prouni/Fies e
 * Pesquisar Cargas): validação do arquivo xlsx, montagem dos corpos enviados
 * ao back, formatação das tabelas e mensagens.
 */

export const TAMANHO_MAXIMO_ARQUIVO = 10 * 1024 * 1024;
export const MENSAGEM_TIPO_ARQUIVO_INVALIDO = 'Tipo de arquivo inválido. (Formatos permitidos: xlsx)';
export const MENSAGEM_TAMANHO_ARQUIVO_INVALIDO = 'Tamanho de arquivo inválido. (Máximo: 10MB)';
export const MENSAGEM_ARQUIVO_NAO_SELECIONADO = 'Nenhum arquivo selecionado.';
export const MENSAGEM_ARQUIVO_NAO_LIDO = 'Não foi possível ler o arquivo selecionado. Tente escolhê-lo novamente.';
export const MENSAGEM_ERRO_PADRAO = 'Não foi possível processar a planilha. Confira o layout e tente novamente.';

export const CONFIRMACAO_SALVAR_CARGA_PBEST = 'Tem certeza que deseja salvar a carga? Os candidatos passam a valer para a inscrição.';
export const CONFIRMACAO_CARREGAR_PROUNI = 'Tem certeza que deseja carregar os aprovados no SAF? Os candidatos passam a valer para a inscrição.';
export const CONFIRMACAO_EXCLUIR_ALUNO_CARGA = 'Tem certeza que deseja excluir este aluno da carga do Prouni/Fies?';
export const MENSAGEM_PROUNI_CONSOLIDADO = 'Planilhas consolidadas com sucesso!';
export const MENSAGEM_ALUNO_EXCLUIDO = 'Aluno excluído da carga com sucesso!';

export const CHAMADAS_PROUNI = ['Primeira Chamada', 'Segunda Chamada', 'Terceira Chamada'];

const EXTENSAO_XLSX = /\.xlsx$/i;

export const SEM_REGISTROS_PBEST = 'Nenhuma carga carregada.';
export const SEM_REGISTROS_PROUNI = 'Nenhum registro carregado.';

/* ---- Arquivo ---- */

/* Mensagem de erro do arquivo (xlsx de até 10 MB) ou '' quando ele é válido. */
export const validarArquivoXlsx = arquivo => {
    if (!arquivo || !arquivo.name || !EXTENSAO_XLSX.test(arquivo.name)) {
        return MENSAGEM_TIPO_ARQUIVO_INVALIDO;
    }

    if (!arquivo.size || arquivo.size > TAMANHO_MAXIMO_ARQUIVO) {
        return MENSAGEM_TAMANHO_ARQUIVO_INVALIDO;
    }

    return '';
};

/* Arquivo lido ({nomeArquivo, conteudo}) no formato do back. */
export const montarArquivo = (nomeArquivo, conteudo) => ({nomeArquivo, conteudo});

/* ---- Datas ---- */

export const dataValida = data => !!data && moment.isMoment(data) && data.isValid();

/* Data do picker no formato do back (YYYY-MM-DD); '' quando vazia ou inválida. */
export const dataParaWire = data => (dataValida(data) ? data.format(FORMATO_DATA_WIRE) : '');

/* ---- Corpos enviados ao back ---- */

export const montarPayloadLeituraPbest = arquivo => ({
    nomeArquivo: arquivo.nomeArquivo,
    conteudo: arquivo.conteudo
});

export const montarPayloadSalvarPbest = ({idProcessoSeletivo, dataChamada, arquivo}) => ({
    idProcessoSeletivo: Number(idProcessoSeletivo),
    dataChamada: dataParaWire(dataChamada),
    nomeArquivo: arquivo.nomeArquivo,
    conteudo: arquivo.conteudo
});

/* Consolidação e carga do Prouni: o arquivo de candidatos é opcional (nulo quando ausente). */
export const montarPayloadProuni = ({idProcessoSeletivo, dataChamada, tipoChamada, resultado, candidatos}) => ({
    idProcessoSeletivo: Number(idProcessoSeletivo),
    dataChamada: dataParaWire(dataChamada),
    tipoChamada,
    resultado: montarArquivo(resultado.nomeArquivo, resultado.conteudo),
    candidatos: candidatos ? montarArquivo(candidatos.nomeArquivo, candidatos.conteudo) : null
});

export const montarPayloadExcelProuni = linhas => ({linhas});

/* ---- Regras de habilitação dos botões ---- */

const preenchido = valor => valor !== null && valor !== undefined && String(valor).trim() !== '';

export const podeSalvarPbest = ({idProcessoSeletivo, dataChamada, arquivo}) =>
    preenchido(idProcessoSeletivo) && dataValida(dataChamada) && !!arquivo;

export const podeConsolidarProuni = ({idProcessoSeletivo, dataChamada, tipoChamada, resultado}) =>
    preenchido(idProcessoSeletivo) && dataValida(dataChamada) && preenchido(tipoChamada) && !!resultado;

/* Pesquisar Cargas: processo de seleção e/ou data (ao menos um); a data, quando digitada, precisa ser válida. */
export const podePesquisarCargas = ({idProcessoSelecao, dataChamada}) =>
    (preenchido(idProcessoSelecao) || dataValida(dataChamada)) && (!dataChamada || dataValida(dataChamada));

/* Parâmetros da pesquisa: só os filtros preenchidos. */
export const montarParametrosPesquisa = ({idProcessoSelecao, dataChamada}) => ({
    ...(preenchido(idProcessoSelecao) ? {idProcessoSelecao: Number(idProcessoSelecao)} : {}),
    ...(dataValida(dataChamada) ? {dataChamada: dataParaWire(dataChamada)} : {})
});

/* ---- Mensagens ---- */

/* Mensagens do erro do back (texto de várias linhas ou objeto com `mensagem`): uma por linha, sem vazias. */
export const separarMensagens = (erro, padrao = MENSAGEM_ERRO_PADRAO) => {
    const texto = typeof erro === 'string' ? erro
        : (erro && typeof erro.mensagem === 'string' ? erro.mensagem : '');
    if (texto.trim().startsWith('<')) {
        return [padrao];
    }

    const mensagens = texto.split(/\r?\n/).map(linha => linha.trim()).filter(Boolean);
    return mensagens.length ? mensagens : [padrao];
};

export const mensagemCargaPbestSalva = quantidade => `Carga salva com sucesso! ${Number(quantidade) || 0} registro(s).`;

/* Aviso do resumo da carga Prouni: sucesso total ou parcial (com falhas). */
export const resumoCargaProuni = resposta => {
    const processados = Number(resposta?.processados) || 0;
    const falhas = Number(resposta?.falhas) || 0;

    if (falhas > 0) {
        return {
            severity: 'warning',
            message: `Carga concluída parcialmente: ${processados} processado(s) e ${falhas} com falha. As críticas estão no Excel.`
        };
    }

    return {severity: 'success', message: `Carga concluída com sucesso! ${processados} registro(s) processado(s).`};
};

/* ---- Formatação das tabelas ---- */

export const formatarCpfCarga = valor => ouTraco(preenchido(valor) ? formatarCpf(valor) : '');

export const formatarDataCarga = valor => formatarData(valor);

/* Número do back (123.4 vira "123,4"); texto passa como veio. */
const formatarNumero = valor => (typeof valor === 'number' ? String(valor).replace('.', ',') : String(valor));

export const formatarPercentual = valor => (preenchido(valor) ? `${formatarNumero(valor)}%` : ouTraco(''));

export const formatarNota = valor => (preenchido(valor) ? formatarNumero(valor) : ouTraco(''));

/* "nomeCurso - codigoCurso" (ou só o que existir). */
export const textoCurso = linha => [linha?.nomeCurso, linha?.codigoCurso].filter(preenchido).join(' - ');

/* O DataGrid exige uma chave por linha (`comparatorKeys`): usa a posição na lista. */
export const comChave = linhas => (Array.isArray(linhas) ? linhas : []).map((linha, indice) => ({...linha, chave: String(indice)}));

/* Linhas da pesquisa com os textos que as colunas filtram (vestibular pela descrição, ou o id sem ela, e curso). */
export const prepararLinhasPesquisa = linhas => (Array.isArray(linhas) ? linhas : []).map(linha => ({
    ...linha,
    textoVestibular: preenchido(linha.descricaoVestibular) ? linha.descricaoVestibular
        : (preenchido(linha.vestibular) ? String(linha.vestibular) : ''),
    curso: textoCurso(linha)
}));

/* Filtro da coluna CPF: aceita o termo com ou sem pontuação. */
export {filtroCpf} from '../processo/processoSelecao.js';

/* Erro de um GET/POST com responseType 'blob' (agora em downloadArquivo.js; re-exportado para os imports existentes). */
export {lerErroDeArquivo} from '../downloadArquivo.js';
